/**
 * Logo easter egg: tapping the WarCarp logo plays one of several reactions.
 * Pure picker (no React, no timers, no storage) so the curated sequence, its
 * stored format and the migration rules are unit-testable;
 * `components/home/logoEgg` turns the choice into motion and saves progress.
 *
 * Every tap plays the next step of one fixed, curated loop (LOGO_SEQUENCE),
 * the same for everyone. Only each user's position in it is saved (locally on
 * the device), so nothing repeats until all 34 have played; after the last the
 * saved progress is cleared and the loop starts again at step 1.
 */

export const LOGO_ENEMIES = ['puffer', 'jelly', 'shark', 'angler', 'crab', 'squid', 'moray', 'urchin', 'seahorse'] as const
export type LogoEnemy = typeof LOGO_ENEMIES[number]

/** The firing reactions and who they shoot: one per enemy, plus three combos. */
export const LOGO_FIRE_TARGETS = {
  'fire-puffer': ['puffer'],
  'fire-jelly': ['jelly'],
  'fire-shark': ['shark'],
  'fire-angler': ['angler'],
  'fire-crab': ['crab'],
  'fire-squid': ['squid'],
  'fire-moray': ['moray'],
  'fire-urchin': ['urchin'],
  'fire-seahorse': ['seahorse'],
  /** Double tap: two enemies in sequence, one shot each. */
  'fire-double': ['jelly', 'crab'],
  /** The rival shark dodges the first shot; the second one lands. */
  'fire-miss': ['shark'],
  /** One round ricochets through all three, each exploding in turn: "x3 COMBO!". */
  'fire-combo': ['puffer', 'angler', 'shark'],
} as const satisfies Record<string, readonly LogoEnemy[]>
export type LogoFireReaction = keyof typeof LOGO_FIRE_TARGETS

/** Every reaction, in catalogue order (LOGO_SEQUENCE is the order they play in). */
export const LOGO_REACTIONS = [
  'fire-puffer', 'fire-jelly', 'fire-shark', 'fire-angler', 'fire-crab', 'fire-squid', 'fire-moray', 'fire-urchin', 'fire-seahorse',
  'fire-double', 'fire-miss', 'fire-combo',
  'wink', 'flip', 'swim', 'bubbles', 'salute', 'shake', 'war', 'chase', 'invasion',
  'dance', 'hula', 'juggle', 'magic', 'skate', 'sneeze',
  'duel', 'waltz', 'rocket', 'balloon', 'disguise', 'lift', 'paperclip',
] as const
export type LogoReaction = typeof LOGO_REACTIONS[number]
export const LOGO_REACTION_LABELS: Record<LogoReaction, string> = {
  'fire-puffer': 'Fire at Puff', 'fire-jelly': 'Fire at Jelly', 'fire-shark': 'Fire at the rival shark', 'fire-angler': 'Fire at the angler',
  'fire-crab': 'Fire at Pinch', 'fire-squid': 'Fire at Inky', 'fire-moray': 'Fire at the moray', 'fire-urchin': 'Fire at Spike',
  'fire-seahorse': 'Fire at Trotter', 'fire-double': 'Double tap', 'fire-miss': 'Miss, then hit', 'fire-combo': 'Ricochet combo',
  wink: 'Wink', flip: 'Jump and flip', swim: 'Swim around', bubbles: 'Blow bubbles',
  salute: 'Helmet salute', shake: 'Shake it off', war: 'WAR!', chase: 'Chase its tail', invasion: 'Invasion wave',
  dance: 'Disco moonwalk', hula: 'Hula hoop', juggle: 'Juggle minnows', magic: 'Magic trick', skate: 'Skateboard', sneeze: 'Sneeze',
  duel: 'Sword fight', waltz: 'Waltz with a lady carp', rocket: 'Bottle rocket', balloon: 'Balloon', disguise: 'Disguise', lift: 'Weightlifting',
  paperclip: 'The helpful paperclip',
}
export const isFireReaction = (reaction: LogoReaction): reaction is LogoFireReaction => reaction in LOGO_FIRE_TARGETS

/** Reduced motion: the gentle in-place wink only. */
export const LOGO_REDUCED_MOTION_REACTION: LogoReaction = 'wink'

/**
 * Gags that stay put (a pose, a prop, a speech bubble) and gags that travel
 * (across the screen, round in circles, flung about). The loop alternates them.
 */
export const LOGO_IN_PLACE: readonly LogoReaction[] = ['salute', 'bubbles', 'wink', 'war', 'hula', 'juggle', 'magic', 'duel', 'disguise', 'lift']
export const LOGO_MOVERS: readonly LogoReaction[] = ['flip', 'swim', 'chase', 'shake', 'dance', 'skate', 'sneeze', 'waltz', 'rocket', 'balloon']
/** The totally ridiculous ones, spread out for surprise. */
export const LOGO_RIDICULOUS: readonly LogoReaction[] = ['dance', 'hula', 'juggle', 'magic', 'skate', 'sneeze', 'duel', 'waltz', 'rocket', 'balloon', 'disguise', 'lift']

/**
 * The loop, the same for everyone: 34 steps, 13 that shoot (the nine single
 * shots, miss then hit, the double tap, the ricochet combo and the invasion
 * wave), the paperclip (no gun, but a grenade, so it is sequenced like a
 * shot) and 20 gags. Rules, in priority order:
 * - The maintainer's opening: 1 cap tip (salute), 2 jump and flip,
 *   3 bubbles, 4 a single shot (Puff).
 * - Never two violent steps (shots, or the paperclip's grenade) back to
 *   back, including the wrap from the invasion (step 34) to the salute
 *   (step 1). With 20 gags for 14 of those, most sit between single gags
 *   and a few gaps hold two.
 * - Among the gags, in-place (LOGO_IN_PLACE) and movers (LOGO_MOVERS) take
 *   strict turns, wrap included, so no two neighbouring gags move alike.
 * - The ridiculous ones are never next to each other and arrive as
 *   surprises: the disco moonwalk right after the first shot (step 5), then
 *   the rest spread through the loop, with the new crowd (waltz, rocket,
 *   sword fight, balloon, disguise, weightlifting) among the old.
 * - The nine single shots come first, each at a different enemy, the four
 *   newcomers (Inky, Spike, the moray, Trotter) mixed in with the originals.
 *   The combos (miss then hit on 26, double tap on 30, the ricochet combo on
 *   32) and the invasion wave are the late treats, the wave as the finale
 *   before the loop calms back down to the salute. The paperclip blows up
 *   late too (step 28), between two calm gags.
 * Changing this list changes LOGO_SEQUENCE_ID, which restarts everyone's
 * progress at step 1 (see parseLogoProgress).
 */
export const LOGO_SEQUENCE = [
  'salute', 'flip', 'bubbles', 'fire-puffer', 'dance', 'fire-jelly', 'wink', 'waltz', 'fire-squid', 'war', 'rocket',
  'fire-shark', 'hula', 'fire-urchin', 'chase', 'duel', 'fire-crab', 'swim', 'juggle', 'fire-moray', 'balloon', 'fire-angler',
  'disguise', 'fire-seahorse', 'sneeze', 'fire-miss', 'magic', 'paperclip', 'shake', 'fire-double', 'lift', 'fire-combo', 'skate', 'invasion',
] as const satisfies readonly LogoReaction[]

/** Every reaction that shoots something: the fire variants, the combos and the wave. */
export const isShootingReaction = (reaction: LogoReaction) => isFireReaction(reaction) || reaction === 'invasion'
/** Shots plus the paperclip's grenade: never two of these in a row in the loop. */
export const isViolentReaction = (reaction: LogoReaction) => isShootingReaction(reaction) || reaction === 'paperclip'

/** Identifies the sequence in stored progress (a short hash of its order). */
export function logoSequenceId(sequence: readonly LogoReaction[] = LOGO_SEQUENCE) {
  let hash = 5381
  for (const ch of sequence.join(',')) hash = (Math.imul(hash, 33) ^ ch.charCodeAt(0)) >>> 0
  return hash.toString(36)
}
export const LOGO_SEQUENCE_ID = logoSequenceId()

export interface LogoEggChoice {
  reaction: LogoReaction; enemies: LogoEnemy[]; reducedMotion: boolean
  /** The step the next tap plays; 0 once the loop is complete (time to clear the saved progress). */
  next: number
}
interface PickOptions { reducedMotion?: boolean; random?: () => number; sequence?: readonly LogoReaction[] }

function pickIndex(length: number, random: () => number) {
  return Math.min(length - 1, Math.max(0, Math.floor(random() * length)))
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const out = [...items]
  for (let i = out.length - 1; i > 0; i--) {
    const j = pickIndex(i + 1, random)
    const swap = out[i]!; out[i] = out[j]!; out[j] = swap
  }
  return out
}

const wrap = (index: number, length: number) => (Number.isFinite(index) && index > 0 && index < length ? Math.floor(index) : 0)

/** Plays step `position` of the loop. Reduced motion only ever winks and does not advance. */
export function chooseLogoReaction(position: number, { reducedMotion = false, random = Math.random, sequence = LOGO_SEQUENCE }: PickOptions = {}): LogoEggChoice {
  const at = wrap(position, sequence.length)
  if (reducedMotion) {
    // The wink is all these users see, so it must not use up a step of the loop.
    return { reaction: LOGO_REDUCED_MOTION_REACTION, enemies: [], reducedMotion, next: at }
  }
  const reaction = sequence[at]!
  // The wave's three invaders vary from wave to wave; every other reaction is fixed.
  const enemies = reaction === 'invasion'
    ? shuffle(LOGO_ENEMIES, random).slice(0, 3)
    : isFireReaction(reaction) ? [...LOGO_FIRE_TARGETS[reaction]] : []
  return { reaction, enemies, reducedMotion, next: wrap(at + 1, sequence.length) }
}

/**
 * Where to carry on once the saved progress has loaded, when `playedBeforeLoad`
 * taps already played steps 0.. from memory: whichever is further along, so
 * neither the saved steps nor the early ones come round again this loop.
 */
export const resumeLogoProgress = (stored: number, playedBeforeLoad: number, length: number = LOGO_SEQUENCE.length) =>
  wrap(Math.max(stored, playedBeforeLoad), length)

/** Storage key for a user's progress (lib/storage adds the app prefix). */
export const logoProgressStorageKey = (userId: string) => `logo-egg-bag_${userId}`

export function serializeLogoProgress(next: number, sequence: readonly LogoReaction[] = LOGO_SEQUENCE) {
  return JSON.stringify({ v: 2, seq: logoSequenceId(sequence), next })
}

/**
 * Reads saved progress as the step the next tap plays; null when there is
 * nothing usable (start at step 1).
 * - Current format for this sequence: that step.
 * - Current format for an older sequence: step 1. We can't tell which
 *   reactions the old position covered, and a reordered loop should open the
 *   way it was curated to; at worst a few reactions come round once more.
 * - The old shuffle-bag format `{order, next}`: the first step of the loop
 *   the user hasn't seen yet, so they don't open with something they've just
 *   watched and nothing unseen is skipped (later steps may repeat once, on
 *   this transitional loop only). A finished or empty bag starts at step 1.
 */
export function parseLogoProgress(raw: string | null | undefined, sequence: readonly LogoReaction[] = LOGO_SEQUENCE): number | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as { v?: unknown; seq?: unknown; order?: unknown; next?: unknown }
    if (typeof value !== 'object' || value === null) return null
    if (value.v === 2) {
      if (typeof value.next !== 'number') return null
      return value.seq === logoSequenceId(sequence) ? wrap(value.next, sequence.length) : 0
    }
    if (Array.isArray(value.order) && value.order.every(r => typeof r === 'string')) {
      const next = typeof value.next === 'number' && Number.isFinite(value.next) ? Math.max(0, Math.floor(value.next)) : 0
      const seen = new Set<string>(value.order.slice(0, next))
      return wrap(sequence.findIndex(r => !seen.has(r)), sequence.length)
    }
    return null
  } catch { return null }
}

/**
 * Seed for where a play's targets line up (lib/logoEggScene): the loop step
 * plus a running play counter, so each play varies but any one is repeatable.
 */
export const logoShotSeed = (step: number, count: number) =>
  (Math.imul(step + 1, 0x9e3779b1) ^ Math.imul(count + 1, 0x85ebca6b)) >>> 0

/** Small deterministic PRNG (mulberry32) for previews and visual frames. */
export function seededRandom(seed: number) {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
